import { env } from '../configs/EnvironmentConfig';

/**
 * Video service for handling Twilio Video API calls
 */

const getVideoBackendUrl = () => {
  // First check environment variable
  if (process.env.REACT_APP_VIDEO_APP_URL) {
    return process.env.REACT_APP_VIDEO_APP_URL;
  }
  // Then check runtime config
  if (env.VIDEO_APP_URL) {
    return env.VIDEO_APP_URL;
  }
  // Fallback to BASE_URL
  return env.BASE_URL || '';
};

/**
 * Get sync token for Twilio Sync to monitor agent connection
 * @param {string} code - Video room code
 * @returns {Promise<Object>} Response with token
 */
export const getSyncToken = async (code) => {
  const backendUrl = getVideoBackendUrl();
  if (!backendUrl) {
    throw new Error('Video backend URL not configured. Please set REACT_APP_VIDEO_APP_URL or configure VIDEO_APP_URL in EnvironmentConfig.');
  }

  try {
    const response = await fetch(`${backendUrl}/client-get-sync-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });
    
    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
        const errorData = JSON.parse(errorText);
        // Check for 403 with "Invalid code" error
        if (response.status === 403 && (errorData.error === 'Invalid code.' || errorData.error?.includes('Invalid code'))) {
          throw new Error('AGENT_NOT_AVAILABLE');
        }
        throw new Error(`Backend returned ${response.status}: ${response.statusText}. ${errorText}`);
      } catch (parseError) {
        if (parseError.message === 'AGENT_NOT_AVAILABLE') {
          throw parseError;
        }
        // If not JSON, check if error text contains "Invalid code"
        if (response.status === 403 && errorText.includes('Invalid code')) {
          throw new Error('AGENT_NOT_AVAILABLE');
        }
        throw new Error(`Backend returned ${response.status}: ${response.statusText}. ${errorText || parseError.message}`);
      }
    }
    
    const data = await response.json();
    
    if (data.error) {
      // Check for "Invalid code" error
      if (data.error === 'Invalid code.' || data.error?.includes('Invalid code')) {
        throw new Error('AGENT_NOT_AVAILABLE');
      }
      throw new Error(data.error);
    }
    
    // Validate token exists
    if (!data.token) {
      console.error('Response structure:', data);
      throw new Error('Invalid response: token not found in response. Backend endpoint may not be configured correctly.');
    }
    
    // Validate token is a string
    if (typeof data.token !== 'string') {
      console.error('Invalid token type:', typeof data.token);
      throw new Error('Invalid token format received from backend.');
    }
    
    return data;
  } catch (error) {
    console.error('Error getting sync token:', error);
    // Preserve AGENT_NOT_AVAILABLE error
    if (error.message === 'AGENT_NOT_AVAILABLE') {
      throw error;
    }
    if (error.message.includes('token not found') || error.message.includes('404')) {
      throw new Error('Video backend endpoint not configured. Please ensure /client-get-sync-token endpoint exists on the backend.');
    }
    throw error;
  }
};

/**
 * Get video token for Twilio Video (after agent connects)
 * @param {string} code - Video room code
 * @returns {Promise<Object>} Response with token
 */
export const getVideoToken = async (code) => {
  const backendUrl = getVideoBackendUrl();
  if (!backendUrl) {
    throw new Error('Video backend URL not configured. Please set REACT_APP_VIDEO_APP_URL or configure VIDEO_APP_URL in EnvironmentConfig.');
  }

  try {
    const response = await fetch(`${backendUrl}/client-get-video-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });
    
    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
        const errorData = JSON.parse(errorText);
        // Check for 403 with "Invalid code" error
        if (response.status === 403 && (errorData.error === 'Invalid code.' || errorData.error?.includes('Invalid code'))) {
          throw new Error('AGENT_NOT_AVAILABLE');
        }
        throw new Error(`Backend returned ${response.status}: ${response.statusText}. ${errorText}`);
      } catch (parseError) {
        if (parseError.message === 'AGENT_NOT_AVAILABLE') {
          throw parseError;
        }
        // If not JSON, check if error text contains "Invalid code"
        if (response.status === 403 && errorText.includes('Invalid code')) {
          throw new Error('AGENT_NOT_AVAILABLE');
        }
        throw new Error(`Backend returned ${response.status}: ${response.statusText}. ${errorText || parseError.message}`);
      }
    }
    
    const data = await response.json();
    
    if (data.error) {
      // Check for "Invalid code" error
      if (data.error === 'Invalid code.' || data.error?.includes('Invalid code')) {
        throw new Error('AGENT_NOT_AVAILABLE');
      }
      throw new Error(data.error);
    }
    
    // Validate token exists
    if (!data.token) {
      console.error('Response structure:', data);
      throw new Error('Invalid response: token not found in response. Backend endpoint may not be configured correctly.');
    }
    
    // Validate token is a string
    if (typeof data.token !== 'string') {
      console.error('Invalid token type:', typeof data.token);
      throw new Error('Invalid token format received from backend.');
    }
    
    return data;
  } catch (error) {
    console.error('Error getting video token:', error);
    // Preserve AGENT_NOT_AVAILABLE error
    if (error.message === 'AGENT_NOT_AVAILABLE') {
      throw error;
    }
    if (error.message.includes('token not found') || error.message.includes('404')) {
      throw new Error('Video backend endpoint not configured. Please ensure /client-get-video-token endpoint exists on the backend.');
    }
    throw error;
  }
};

/**
 * Trigger a video call - Creates everything and returns URL/code
 * This is an alternative to manually generating codes and getting tokens
 * @param {Object} options - Optional parameters (e.g., agentId, userId)
 * @returns {Promise<Object>} Response with room code, URL, or tokens
 */
export const triggerVideoCall = async (options = {}) => {
  const backendUrl = getVideoBackendUrl();
  if (!backendUrl) {
    throw new Error('Video backend URL not configured. Please set REACT_APP_VIDEO_APP_URL or configure VIDEO_APP_URL in EnvironmentConfig.');
  }

  try {
    const response = await fetch(`${backendUrl}/trigger-video-call`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Backend returned ${response.status}: ${response.statusText}. ${errorText}`);
    }
    
    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    return data;
  } catch (error) {
    console.error('Error triggering video call:', error);
    throw error;
  }
};

/**
 * Generate a video room code
 * @returns {Promise<string>} Video room code
 */
export const generateVideoRoomCode = async () => {
  // This could be implemented to call a backend endpoint
  // For now, generate a simple code
  return Math.random().toString(36).substring(2, 8).toUpperCase();
};

